import * as React from "react";
import { css } from "@emotion/react";
import Link from "next/link";
import {
  fontSize,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { textColor } from "main/javascripts/styles/base/colorStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";

interface BreadcrumbItem {
  label?: string;
  url?: string;
  links?: Array<{ label: string; url: string }>;
}

export interface IProps {
  items: BreadcrumbItem[];
}

export const Breadcrumb: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  return (
    <ul css={blockStyle}>
      {props.items.map((item, index) => (
        <li key={index} css={listStyle}>
          {item.links ? (
            <div css={multiLinkStyle}>
              {item.links.map((link, linkIndex) => (
                <React.Fragment key={linkIndex}>
                  {linkIndex > 0 && <span css={separatorStyle}>/</span>}
                  <Link href={link.url} passHref>
                    <a css={linkStyle}>{link.label}</a>
                  </Link>
                </React.Fragment>
              ))}
            </div>
          ) : item.url && item.label ? (
            <Link href={item.url} passHref>
              <a css={linkStyle}>{item.label}</a>
            </Link>
          ) : item.label ? (
            <span css={textStyle}>{item.label}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
};

const blockStyle = css`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: ${space.atom} 0;
`;

const listStyle = css`
  display: inline-flex;
  align-items: center;
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${textColor.secondaryDarkColor};

  &:not(:last-child)::after {
    content: "";
    width: 0.4rem;
    height: 0.4rem;
    border-top: 1px solid currentColor;
    border-right: 1px solid currentColor;
    transform: rotate(45deg);
    margin: 0 ${space.atom2x};
    opacity: 0.5;
  }
`;

const linkStyle = css`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;

const textStyle = css`
  color: inherit;
`;

const multiLinkStyle = css`
  display: inline-flex;
  align-items: center;
  gap: ${space.atom};
`;

const separatorStyle = css`
  color: inherit;
  opacity: 0.5;
`;
